/**
 * FSD Part 3 — Treasury (docs §19161). C17: one section per FSD feature
 * specification — 3.2.1 (cash, bank, beneficiaries, mandates, bank
 * reconciliation), 3.2.2 (wallets), 3.2.3 (debt facilities, covenants),
 * 3.2.4 (forecasting). Section titles come from `nav.fsd.*`.
 *
 * Treasury's specifications carry no clean feature code in the FSD, so these
 * sections omit `code` and cite only their section number.
 *
 * Currencies, FX rates and the FX tolerance-hold queue are NOT here: they are
 * Enterprise configuration (FSD 1.2.7) and live in `p01-enterprise.ts`.
 *
 * Item ids are stable and must stay so: favourites and recents in
 * localStorage key off them. Sections moved; items did not change.
 */
import type { NavGroup } from '../types'
import { BANKREC_PERMISSIONS } from '@/modules/bankrec/permissions'
import { CASH_MOVEMENT_ANOMALIES_PATH } from '@/modules/cash/anomalies/routes'
import { CASH_PERMISSIONS } from '@/modules/cash/permissions'
import { CASH_TRANSFER_PATHS } from '@/modules/cash/transfers/routes'
import { CASH_C4_PERMISSIONS } from '@/modules/cash/transfers/permissions'
import { WALLET_FUNDING_PERMISSIONS } from '@/modules/treasury/wallet-funding/permissions'
import { TREASURY_WALLET_FUNDING_PATH } from '@/modules/treasury/wallet-funding/routes'
import { TREASURY_PERMISSIONS } from '@/modules/treasury/permissions'
import { SWEEP_PERMISSIONS } from '@/modules/treasury/concentration/permissions'
import { BANK_RELATIONSHIP_PERMISSIONS } from '@/modules/treasury/bank-relationships/permissions'
import { BANK_CONNECTION_PERMISSIONS } from '@/modules/treasury/bank-connections/permissions'
import { TREASURY_BANK_CONNECTIONS_PATH } from '@/modules/treasury/bank-connections/routes'
import { RECON_EXCEPTION_PERMISSIONS } from '@/modules/treasury/reconciliation-exceptions/permissions'
import { TREASURY_RECONCILIATION_EXCEPTIONS_PATH } from '@/modules/treasury/reconciliation-exceptions/routes'
import { IC_INSTRUMENT_PERMISSIONS } from '@/modules/treasury/intercompany/permissions'
import { TREASURY_INTERCOMPANY_PATH } from '@/modules/treasury/intercompany/routes'
import { SCREENING_PERMISSIONS } from '@/modules/treasury/screening/permissions'
import { TREASURY_SCREENING_PATH } from '@/modules/treasury/screening/routes'

export const P03_TREASURY_GROUPS: NavGroup[] = [
  {
    id: 's-3.2.1',
    kind: 'feature',
    fsd: '3.2.1',
    labelKey: 'nav.fsd.s3_2_1.short',
    fullTitleKey: 'nav.fsd.s3_2_1.full',
    items: [
      /* FT4 (R030/R047/R062): the v1 cash position, one-step transfers and the
       * live 13-week forecast are gone. Their screens are the treasury
       * positions, the governed transfer instructions and the persisted
       * forecast runs below; the old paths redirect (`legacyRedirects.ts`). */
      {
        id: 'cash-accounts',
        labelKey: 'cash.accounts.title',
        to: '/cash/accounts',
        icon: 'building',
        permission: CASH_PERMISSIONS.accountRead,
        descriptionKey: 'nav.items.cashAccounts.description',
      },
      {
        id: 'cash-transfer-instructions',
        labelKey: 'treasury.routeTitles.instructions',
        to: CASH_TRANSFER_PATHS.instructions,
        icon: 'refresh',
        permission: CASH_C4_PERMISSIONS.transferRead,
        descriptionKey: 'nav.items.cashTransferInstructions.description',
      },
      {
        id: 'cash-funds-in-transit',
        labelKey: 'treasury.routeTitles.fundsInTransit',
        to: CASH_TRANSFER_PATHS.fundsInTransit,
        icon: 'history',
        permission: CASH_C4_PERMISSIONS.transferRead,
        descriptionKey: 'nav.items.cashFundsInTransit.description',
      },
      {
        id: 'treasury-positions',
        labelKey: 'treasury.routeTitles.positions',
        to: '/treasury/positions',
        icon: 'dashboard',
        permission: TREASURY_PERMISSIONS.positionRead,
        descriptionKey: 'nav.items.treasuryPositions.description',
      },
      /* FT11-U37 (FSD 3.2.1 S05, FIN-T01-R016/R018). The group cash position across legal
       * entities for a perimeter and reporting currency. Gated on `treasury.position.group.read`. */
      {
        id: 'treasury-position-group',
        labelKey: 'treasury.routeTitles.positionGroup',
        to: '/treasury/positions/group',
        icon: 'sitemap',
        permission: TREASURY_PERMISSIONS.positionGroupRead,
        code: 'FIN-T01-R016',
      },
      /* FT11-U37 (FSD 3.2.1, FIN-T01-R019). Liquidity Analysis: available liquidity per legal
       * entity with every deduction shown. Gated on `treasury.position.read`. */
      {
        id: 'treasury-liquidity',
        labelKey: 'treasury.routeTitles.liquidity',
        to: '/treasury/liquidity',
        icon: 'scale',
        permission: TREASURY_PERMISSIONS.positionRead,
        code: 'FIN-T01-R019',
      },
      /* FT15-U39 (FSD 3.2.1, FIN-T01-R020). Cash concentration: sweep structures, their
       * simulation and execution, deferrals and history, on one screen. Gated on
       * `treasury.sweep.read`; the writes are gated in the screen. */
      {
        id: 'treasury-concentration',
        labelKey: 'treasury.routeTitles.concentration',
        to: '/treasury/concentration',
        icon: 'refresh',
        permission: SWEEP_PERMISSIONS.read,
        code: 'FIN-T01-R020',
      },
      /* FT19-U44 (FSD 3.2.1, FIN-T01-R063). The bank relationship register: each bank's accounts,
       * facilities, contacts, tariff and limits, with concentration by counterparty bank. */
      {
        id: 'treasury-bank-relationships',
        labelKey: 'treasury.bankRelationships.nav.label',
        to: '/treasury/bank-relationships',
        icon: 'landmark',
        permission: BANK_RELATIONSHIP_PERMISSIONS.read,
        code: 'FIN-T01-R063',
        descriptionKey: 'treasury.bankRelationships.nav.description',
      },
      {
        id: 'treasury-beneficiaries',
        labelKey: 'treasury.routeTitles.beneficiaries',
        to: '/treasury/beneficiaries',
        icon: 'building',
        permission: TREASURY_PERMISSIONS.beneficiaryRead,
        descriptionKey: 'nav.items.treasuryBeneficiaries.description',
      },
      {
        id: 'treasury-beneficiary-changes',
        labelKey: 'treasury.routeTitles.beneficiaryChanges',
        to: '/treasury/beneficiary-changes',
        icon: 'shield',
        permission: TREASURY_PERMISSIONS.beneficiaryRead,
      },
      {
        id: 'treasury-mandates',
        labelKey: 'treasury.routeTitles.mandates',
        to: '/treasury/mandates',
        icon: 'check',
        permission: TREASURY_PERMISSIONS.mandateRead,
        descriptionKey: 'nav.items.treasuryMandates.description',
      },
      {
        id: 'treasury-settings',
        labelKey: 'treasury.routeTitles.settings',
        to: '/treasury/settings',
        icon: 'settings',
        permission: TREASURY_PERMISSIONS.settingsManage,
        descriptionKey: 'nav.items.treasurySettings.description',
      },
      {
        id: 'bankrec-sessions',
        labelKey: 'bankrec.routeTitles.sessions',
        to: '/bankrec/sessions',
        icon: 'check',
        permission: BANKREC_PERMISSIONS.sessionRead,
        descriptionKey: 'nav.items.bankrecSessions.description',
      },
      {
        id: 'bankrec-imports',
        labelKey: 'bankrec.routeTitles.imports',
        to: '/bankrec/imports',
        icon: 'download',
        permission: BANKREC_PERMISSIONS.statementRead,
        descriptionKey: 'nav.items.bankrecImports.description',
      },
      /* FT7: the mapping profiles left the sidebar and are a tab of the reconciliation
       * screens (a `/bankrec/profiles` entry in `nav/aliases.ts`, the BankrecNav strip); the
       * `bankrec-profiles` item id went with them. Bank connections are where a statement's channel is configured. */
      {
        id: 'treasury-bank-connections',
        labelKey: 'treasury.routeTitles.bankConnections',
        to: TREASURY_BANK_CONNECTIONS_PATH,
        icon: 'download',
        permission: BANK_CONNECTION_PERMISSIONS.read,
        code: 'TRS-S02',
      },
      /* FT9-U36 (FSD S04: FIN-T01-R015, V042, V043). The reconciliation exception register and
       * the Bank Reconciliation Dashboard: assign, investigate and resolve what the match
       * engine could not settle. Gated on `bankrec.exception.read`; acting needs `.manage`. */
      {
        id: 'treasury-reconciliation-exceptions',
        labelKey: 'treasury.routeTitles.reconciliationExceptions',
        to: TREASURY_RECONCILIATION_EXCEPTIONS_PATH,
        icon: 'alert',
        permission: RECON_EXCEPTION_PERMISSIONS.read,
        code: 'TRS-S04',
      },
      /* FT18 (FSD 3.2.1.9, AI-T01-07). Treasury movement anomalies: the scan's findings, each naming
       * its comparison, and their disposition by someone other than the movement's recorder. Gated on
       * `cash.account.read`; scan and disposition need `cash.account.update` (gated in the screen). */
      {
        id: 'cash-movement-anomalies',
        labelKey: 'cash.movementAnomalies.routeTitle',
        to: CASH_MOVEMENT_ANOMALIES_PATH,
        icon: 'alert',
        permission: CASH_PERMISSIONS.accountRead,
        code: 'AI-T01-07',
      },
      /* FT8-U38. Intercompany instruments (FSD R032: the loan, advance and current-account
       * agreements between two legal entities, and the intercompany loan register). */
      {
        id: 'treasury-intercompany-instruments',
        labelKey: 'treasury.routeTitles.intercompanyInstruments',
        to: TREASURY_INTERCOMPANY_PATH,
        icon: 'sitemap',
        permission: IC_INSTRUMENT_PERMISSIONS.read,
        code: 'FIN-T01-R032',
      },
      /* The compliance screening lists with the exchange-control rules as its second tab
       * (R055, V085). It is a screen of its own, gated on `cash.screening.manage`, not an
       * alias of the transfer instructions (DECISIONS.md, FT8). */
      {
        id: 'treasury-screening',
        labelKey: 'treasury.routeTitles.screeningLists',
        to: TREASURY_SCREENING_PATH,
        icon: 'shield',
        permission: SCREENING_PERMISSIONS.manage,
        code: 'FIN-T01-R055',
      },
    ],
  },
  {
    id: 's-3.2.2',
    kind: 'feature',
    fsd: '3.2.2',
    labelKey: 'nav.fsd.s3_2_2.short',
    fullTitleKey: 'nav.fsd.s3_2_2.full',
    items: [
      {
        id: 'treasury-wallets',
        labelKey: 'treasury.routeTitles.wallets',
        to: '/treasury/wallets',
        icon: 'building',
        permission: TREASURY_PERMISSIONS.walletRead,
        descriptionKey: 'nav.items.treasuryWallets.description',
      },
      /* The wallet approval queue: a queue an approver opens directly, so it
       * needs its own door. It reads on `treasury.wallet.approve`, not
       * `treasury.wallet.read`: the screen exists to act on, and the wallets
       * list above already serves everyone who only needs to look. That also
       * keeps it off the sidebar of the custodian who captures the counts,
       * which is the SoD pair the queue exists to enforce. */
      {
        id: 'treasury-wallet-approvals',
        labelKey: 'treasury.routeTitles.walletApprovals',
        to: '/treasury/wallet-approvals',
        icon: 'check',
        permission: TREASURY_PERMISSIONS.walletApprove,
      },
      /* Wallet funding: the requests to top a wallet up, the decision on each
       * and the transfer instruction that carries the money (TRS R023). */
      {
        id: 'treasury-wallet-funding',
        labelKey: 'treasury.routeTitles.walletFunding',
        to: TREASURY_WALLET_FUNDING_PATH,
        icon: 'building',
        permission: WALLET_FUNDING_PERMISSIONS.fundingRead,
      },
      {
        id: 'treasury-wallet-expenditure',
        labelKey: 'treasury.routeTitles.walletExpenditure',
        to: '/treasury/wallet-expenditure',
        icon: 'building',
        permission: TREASURY_PERMISSIONS.walletRead,
      },
      {
        id: 'treasury-wallet-counts',
        labelKey: 'treasury.routeTitles.walletCounts',
        to: '/treasury/wallet-counts',
        icon: 'scale',
        permission: TREASURY_PERMISSIONS.walletRead,
      },
      {
        id: 'treasury-wallet-balances',
        labelKey: 'treasury.routeTitles.walletBalances',
        to: '/treasury/reports/wallet-balances',
        icon: 'scale',
        permission: TREASURY_PERMISSIONS.walletRead,
      },
    ],
  },
  {
    id: 's-3.2.3',
    kind: 'feature',
    fsd: '3.2.3',
    labelKey: 'nav.fsd.s3_2_3.short',
    fullTitleKey: 'nav.fsd.s3_2_3.full',
    items: [
      {
        id: 'treasury-facilities',
        labelKey: 'treasury.routeTitles.facilities',
        to: '/treasury/facilities',
        icon: 'history',
        permission: TREASURY_PERMISSIONS.debtRead,
        descriptionKey: 'nav.items.treasuryFacilities.description',
      },
      {
        id: 'treasury-covenants',
        labelKey: 'treasury.routeTitles.covenants',
        to: '/treasury/covenants',
        icon: 'check',
        permission: TREASURY_PERMISSIONS.debtRead,
      },
      {
        id: 'treasury-debt-maturity',
        labelKey: 'treasury.routeTitles.debtMaturity',
        to: '/treasury/reports/debt-maturity',
        icon: 'calendar',
        permission: TREASURY_PERMISSIONS.debtRead,
      },
      {
        id: 'treasury-accrual-runs',
        labelKey: 'treasury.routeTitles.accrualRuns',
        to: '/treasury/accrual-runs',
        icon: 'refresh',
        permission: TREASURY_PERMISSIONS.debtRead,
      },
    ],
  },
  {
    id: 's-3.2.4',
    kind: 'feature',
    fsd: '3.2.4',
    labelKey: 'nav.fsd.s3_2_4.short',
    fullTitleKey: 'nav.fsd.s3_2_4.full',
    items: [
      /* Recurring cash items (§8.5) — the standing rents, loan instalments and
       * subscriptions the 13-week forecast is built from. Maintained master
       * data, not a document reached from one, so the sidebar is its door. */
      {
        id: 'cash-recurring-items',
        labelKey: 'cash.recurring.title',
        to: '/cash/recurring-items',
        icon: 'refresh',
        permission: CASH_PERMISSIONS.recurringRead,
      },
      {
        id: 'treasury-forecasts',
        labelKey: 'treasury.routeTitles.forecasts',
        to: '/treasury/forecasts',
        icon: 'history',
        permission: TREASURY_PERMISSIONS.forecastRead,
        descriptionKey: 'nav.items.treasuryForecasts.description',
      },
      {
        id: 'treasury-scenarios',
        labelKey: 'treasury.routeTitles.scenarios',
        to: '/treasury/forecast-scenarios',
        icon: 'sitemap',
        permission: TREASURY_PERMISSIONS.forecastRead,
      },
      {
        id: 'treasury-forecast-accuracy',
        labelKey: 'treasury.routeTitles.forecastAccuracy',
        to: '/treasury/reports/forecast-accuracy',
        icon: 'check',
        permission: TREASURY_PERMISSIONS.forecastRead,
      },
      {
        id: 'treasury-liquidity-gap',
        labelKey: 'treasury.routeTitles.liquidityGap',
        to: '/treasury/reports/liquidity-gap',
        icon: 'scale',
        permission: TREASURY_PERMISSIONS.forecastRead,
      },
    ],
  },
]
